import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { CustomLink } from '@shared/custom_link';

import { accountBaseUrl } from '@utils/environment_urls';

import iconCloseWhite from '@images/home/icon-close-white.svg';
import iconHamburgerDark from '@images/home/icon-hamburger-dark.svg';
import iconHamburgerWhite from '@images/home/icon-hamburger-white.svg';
import logoEndorserBlack from '@images/home/logo-clutter-endorser-black.png';
import logoEndorserWhite from '@images/home/logo-clutter-endorser-white.png';

import { HOME_NAV_SECTIONS, type NavSectionId } from './nav_sections';
import { HomeQuoteCta } from './quote_cta';
import {
  GhostButton,
  SectionContainer,
  secondaryOnDarkButtonStyles,
} from './styles';
import { MEDIA, TOKENS } from './tokens';
import { useNavAppearance } from './use_nav_appearance';
import { useNavScroll } from './use_nav_scroll';

const MOBILE_HEADER_CONTROL_SIZE = '48px';
const MOBILE_MENU_ICON_BUTTON_SIZE = '24px';
const MOBILE_SCROLLED_NAV_WIDTH = '386px';
const MOBILE_SCROLLED_NAV_HEIGHT = '64px';

const HeaderWrap = styled.header<{ $solid: boolean; $menuOpen: boolean }>`
  position: fixed;
  top: 0;
  z-index: ${({ $menuOpen }) => ($menuOpen ? 110 : 70)};
  width: 100%;
  padding: ${TOKENS.space[8]} 0 0;
  filter: ${({ $solid, $menuOpen }) =>
    $solid && !$menuOpen
      ? 'drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.06))'
      : 'none'};
  transition:
    border-color 0.3s ease,
    filter 0.3s ease;

  ${MEDIA.lg} {
    padding-left: ${TOKENS.space[48]};
    padding-right: ${TOKENS.space[48]};
    transition-duration: 0.15s;
  }
`;

/** Figma scrolled mobile bar is exactly 386×64 — size the shell, not only the inner. */
const HeaderSectionContainer = styled(SectionContainer)<{
  $scrolledMobile: boolean;
}>`
  ${({ $scrolledMobile }) =>
    $scrolledMobile &&
    `
    width: ${MOBILE_SCROLLED_NAV_WIDTH};
    max-width: calc(100% - 16px);
    margin-left: auto;
    margin-right: auto;
  `}

  ${MEDIA.lg} {
    width: 100%;
    max-width: ${TOKENS.sectionMaxWidth};
  }
`;

const HeaderInner = styled.div<{ $solid: boolean; $menuOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  border-radius: ${TOKENS.radius.md};
  /* Use ring shadow so the 1px stroke doesn’t add +2px (was 66). */
  border: 0;
  box-shadow: ${({ $solid, $menuOpen }) =>
    $menuOpen || !$solid ? 'none' : `0 0 0 1px ${TOKENS.color.border}`};
  background: ${({ $solid, $menuOpen }) =>
    $menuOpen ? 'transparent' : $solid ? '#ffffff' : 'transparent'};
  backdrop-filter: ${({ $solid, $menuOpen }) =>
    $solid && !$menuOpen ? 'blur(8px)' : 'none'};
  padding: ${TOKENS.space[8]} ${TOKENS.space[24]};
  height: ${({ $solid, $menuOpen }) =>
    $solid && !$menuOpen ? MOBILE_SCROLLED_NAV_HEIGHT : undefined};
  min-height: ${({ $solid, $menuOpen }) =>
    $solid && !$menuOpen ? MOBILE_SCROLLED_NAV_HEIGHT : undefined};
  transition: ${({ $menuOpen }) =>
    $menuOpen
      ? 'none'
      : 'background 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease'};

  ${MEDIA.lg} {
    margin-left: calc(-1 * (${TOKENS.space[16]} + 4px));
    margin-right: calc(-1 * (${TOKENS.space[16]} + 4px));
    padding: ${TOKENS.space[8]} ${TOKENS.space[16]};
    height: auto;
    min-height: 0;
    border: 1px solid
      ${({ $solid, $menuOpen }) =>
        $menuOpen || !$solid ? 'transparent' : TOKENS.color.border};
    box-shadow: none;
    transition-duration: 0.15s;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${TOKENS.space[8]};
  min-height: ${MOBILE_HEADER_CONTROL_SIZE};

  ${MEDIA.lg} {
    gap: ${TOKENS.space[48]};
    min-height: 0;
  }
`;

const LogoNavGroup = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: ${TOKENS.space[8]};
  min-height: ${MOBILE_HEADER_CONTROL_SIZE};

  ${MEDIA.lg} {
    min-height: 0;
  }
`;

const LogoLink = styled(CustomLink)`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  line-height: 0;
`;

const LogoImage = styled.img`
  display: block;
  height: 32px;
  width: auto;
  max-width: 108px;
  object-fit: contain;
  object-position: left center;

  ${MEDIA.lg} {
    height: 36px;
    width: 114px;
    max-width: none;
    min-width: 114px;
  }
`;

const Nav = styled.nav`
  display: none;
  align-items: center;

  ${MEDIA.lg} {
    display: flex;
  }
`;

const NavLink = styled.a<{ $solid: boolean; $active?: boolean }>`
  border-radius: ${TOKENS.radius.sm};
  padding: ${TOKENS.space[12]} ${TOKENS.space[16]};
  font-family: ${TOKENS.font.sans};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: none;
  color: ${({ $solid }) =>
    $solid ? TOKENS.color.foreground : TOKENS.color.onDark};
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${({ $solid }) =>
      $solid ? TOKENS.color.foreground : TOKENS.color.inverse};
    background: ${({ $solid }) =>
      $solid ? 'rgba(0, 0, 0, 0.04)' : TOKENS.color.neutral0_10};
  }

  &:active {
    background: ${({ $solid }) =>
      $solid ? 'rgba(0, 0, 0, 0.06)' : TOKENS.color.neutral0_12};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${TOKENS.space[8]};
  min-height: ${MOBILE_HEADER_CONTROL_SIZE};

  ${MEDIA.lg} {
    min-height: 0;
  }
`;

const MOBILE_NAV_ICON_SIZE = '20px';
const MOBILE_NAV_CLOSE_ICON_SIZE = '24px';

const MobileHeaderQuote = styled.div<{ $visible: boolean; $menuOpen: boolean }>`
  display: ${({ $visible, $menuOpen }) =>
    $visible && !$menuOpen ? 'flex' : 'none'};
  align-items: center;
  align-self: center;
  min-width: 0;
  height: ${MOBILE_HEADER_CONTROL_SIZE};

  ${MEDIA.lg} {
    display: none;
  }

  /* Figma: Get quote, Hug × 48, space-24 horizontal padding */
  && > a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: ${MOBILE_HEADER_CONTROL_SIZE};
    min-height: ${MOBILE_HEADER_CONTROL_SIZE};
    padding: 0 ${TOKENS.space[24]};
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
  }
`;

const MobileNavIconButton = styled.button<{
  $solid?: boolean;
  $menuOpen?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: center;
  /* Closed: compact 24px so right inset matches left (Figma). Open: 36px close. */
  width: ${({ $menuOpen }) =>
    $menuOpen ? MOBILE_HEADER_CONTROL_SIZE : MOBILE_MENU_ICON_BUTTON_SIZE};
  height: ${({ $menuOpen }) =>
    $menuOpen ? MOBILE_HEADER_CONTROL_SIZE : MOBILE_MENU_ICON_BUTTON_SIZE};
  border: ${({ $menuOpen }) =>
    $menuOpen ? `1px solid ${TOKENS.color.border}` : '0'};
  border-radius: ${({ $menuOpen }) =>
    $menuOpen ? TOKENS.radius.md : TOKENS.radius.sm};
  background: ${({ $menuOpen }) =>
    $menuOpen ? 'rgba(249, 250, 251, 0.07)' : 'transparent'};
  padding: 0;
  margin: 0;
  cursor: pointer;
  appearance: none;

  ${MEDIA.lg} {
    display: none;
  }

  img {
    display: block;
    width: ${({ $menuOpen }) =>
      $menuOpen ? MOBILE_NAV_CLOSE_ICON_SIZE : MOBILE_NAV_ICON_SIZE};
    height: ${({ $menuOpen }) =>
      $menuOpen ? MOBILE_NAV_CLOSE_ICON_SIZE : MOBILE_NAV_ICON_SIZE};
    object-fit: contain;
    object-position: center;
  }

  &:hover {
    background: ${({ $solid = false, $menuOpen = false }) => {
      if ($menuOpen) {
        return TOKENS.color.neutral0_10;
      }

      return $solid ? 'rgba(0, 0, 0, 0.04)' : TOKENS.color.neutral0_10;
    }};
  }

  &:active {
    background: ${({ $solid = false, $menuOpen = false }) => {
      if ($menuOpen) {
        return TOKENS.color.neutral0_12;
      }

      return $solid ? 'rgba(0, 0, 0, 0.06)' : TOKENS.color.neutral0_12;
    }};
  }

  &:focus:not(.focus-visible) {
    outline: none;
  }
`;

const DesktopClientPortal = styled(GhostButton)<{ $solid?: boolean }>`
  display: none;
  && {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
  }
  color: ${({ $solid }) =>
    $solid ? TOKENS.color.foreground : TOKENS.color.onDark};

  ${MEDIA.lg} {
    display: inline-flex;
  }

  &:hover {
    color: ${({ $solid }) =>
      $solid ? TOKENS.color.foreground : TOKENS.color.inverse};
    background: ${({ $solid }) =>
      $solid ? 'rgba(0, 0, 0, 0.04)' : TOKENS.color.neutral0_10};
  }

  &:active {
    background: ${({ $solid }) =>
      $solid ? 'rgba(0, 0, 0, 0.06)' : TOKENS.color.neutral0_12};
  }
`;

const DesktopHeaderCta = styled.div`
  display: none;

  ${MEDIA.lg} {
    display: block;
  }
`;

const MobileNavOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  background: ${TOKENS.color.sectionBgDark};
  animation: home-mobile-nav-overlay-in 300ms cubic-bezier(0.16, 1, 0.3, 1) both;

  ${MEDIA.lg} {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MobileNavBody = styled.div<{ $headerOffset: number }>`
  display: flex;
  flex: 1;
  justify-content: center;
  padding: ${TOKENS.space[24]} ${TOKENS.space[32]};
  padding-top: ${({ $headerOffset }) => $headerOffset}px;
`;

const MobileNavContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${TOKENS.space[16]};
  animation: home-mobile-nav-content-in 300ms cubic-bezier(0.16, 1, 0.3, 1) 50ms
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MobileNavLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  border-radius: ${TOKENS.radius.sm};
  padding: 2px ${TOKENS.space[24]};
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
  color: ${TOKENS.color.inverse};
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: ${TOKENS.color.neutral0_7};
  }

  &:active {
    background: ${TOKENS.color.neutral0_10};
  }
`;

const MobilePortalButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 52px;
  border-radius: ${TOKENS.radius.sm};
  padding: 2px ${TOKENS.space[24]};
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 12px;
  text-decoration: none;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    filter 0.15s ease;

  ${secondaryOnDarkButtonStyles};
`;

const MobileQuoteWrap = styled.div`
  width: 100%;

  & > a {
    display: flex;
    width: 100%;
    min-height: 48px;
    justify-content: center;
    align-items: center;
    padding: ${TOKENS.space[16]} ${TOKENS.space[24]};
    font-size: 14px;
    font-weight: 600;
    line-height: 12px;
  }
`;

const getFocusableElements = (container: HTMLElement) =>
  Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => element.offsetParent !== null);

export const HomeHeader: React.FC = () => {
  const appearance = useNavAppearance();
  const solid = appearance !== 'hero';
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(72);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const didMountMenuRef = useRef(false);
  const { activeSection, scrollToSection } = useNavScroll();

  const updateHeaderOffset = () => {
    if (!headerRef.current) {
      return;
    }

    setHeaderOffset(headerRef.current.offsetHeight);
  };

  useEffect(() => {
    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);

    return () => window.removeEventListener('resize', updateHeaderOffset);
  }, [menuOpen, solid]);

  const handleNavClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    navId: NavSectionId,
    external: boolean,
    onNavigate?: () => void,
  ) => {
    if (external) {
      onNavigate?.();
      return;
    }

    event.preventDefault();
    scrollToSection(navId);
    onNavigate?.();
  };

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    menuButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = [
        ...(headerRef.current ? getFocusableElements(headerRef.current) : []),
        ...(overlayRef.current ? getFocusableElements(overlayRef.current) : []),
      ];

      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!didMountMenuRef.current) {
      didMountMenuRef.current = true;
      return;
    }

    menuButtonRef.current?.focus();
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <HeaderWrap ref={headerRef} $solid={solid} $menuOpen={menuOpen}>
        <HeaderSectionContainer $scrolledMobile={solid && !menuOpen}>
          <HeaderInner $solid={solid} $menuOpen={menuOpen}>
            <HeaderRow>
              <LogoNavGroup>
                <LogoLink to="/" aria-label="Clutter home" onClick={closeMenu}>
                  <LogoImage
                    src={
                      menuOpen || !solid ? logoEndorserWhite : logoEndorserBlack
                    }
                    alt="Clutter by Iron Mountain"
                  />
                </LogoLink>

                <Nav aria-label="Main">
                  {HOME_NAV_SECTIONS.map(({ id, label, href, external }) => (
                    <NavLink
                      key={id}
                      href={href}
                      $solid={solid}
                      $active={
                        !solid && activeSection !== null && activeSection === id
                      }
                      aria-current={
                        !solid && activeSection === id ? 'true' : undefined
                      }
                      onClick={(event) => handleNavClick(event, id, external)}
                    >
                      {label}
                    </NavLink>
                  ))}
                </Nav>
              </LogoNavGroup>

              <Actions>
                <DesktopClientPortal $solid={solid} href={accountBaseUrl()}>
                  Client portal
                </DesktopClientPortal>
                <DesktopHeaderCta>
                  <HomeQuoteCta />
                </DesktopHeaderCta>
                <MobileHeaderQuote $visible={solid} $menuOpen={menuOpen}>
                  <HomeQuoteCta />
                </MobileHeaderQuote>
                <MobileNavIconButton
                  ref={menuButtonRef}
                  type="button"
                  $solid={solid}
                  $menuOpen={menuOpen}
                  aria-expanded={menuOpen}
                  aria-controls="mobile-nav-menu"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  <img
                    src={
                      menuOpen
                        ? iconCloseWhite
                        : solid
                          ? iconHamburgerDark
                          : iconHamburgerWhite
                    }
                    alt=""
                  />
                </MobileNavIconButton>
              </Actions>
            </HeaderRow>
          </HeaderInner>
        </HeaderSectionContainer>
      </HeaderWrap>

      {menuOpen && (
        <MobileNavOverlay
          ref={overlayRef}
          id="mobile-nav-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
        >
          <MobileNavBody $headerOffset={headerOffset}>
            <MobileNavContent>
              <nav aria-label="Main">
                {HOME_NAV_SECTIONS.map(({ id, label, href, external }) => (
                  <MobileNavLink
                    key={id}
                    href={href}
                    onClick={(event) =>
                      handleNavClick(event, id, external, closeMenu)
                    }
                  >
                    {label}
                  </MobileNavLink>
                ))}
              </nav>

              <MobilePortalButton
                href={accountBaseUrl()}
                onClick={(event) => event.stopPropagation()}
              >
                Client portal
              </MobilePortalButton>
              <MobileQuoteWrap>
                <HomeQuoteCta
                  inMobileMenu
                  onClick={(event) => event.stopPropagation()}
                />
              </MobileQuoteWrap>
            </MobileNavContent>
          </MobileNavBody>
        </MobileNavOverlay>
      )}
    </>
  );
};
